import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import InvitationTypeForm from '../../_components/InvitationTypeForm/InvitationTypeForm';
import InvitationTypeCard from '../../_components/InvitationTypeCard';
import InvitationPreviewV2 from '../../_components/InvitationPreview/InvitationPreviewV2';
import type { Event, InvitationType, Guest } from '../../_graphql/generated/types';
import { AttendanceStatus } from '../../_graphql/generated/types';
import { eventsApi, invitationTypesApi, guestsApi } from '../../_lib/api-client';
import GuestList from '../guests/GuestList';
import InvitationDesignPage from '../invitations/InvitationDesignPage';
import { InvitationDesign } from '../../_types/invitation-design-v2';
import Analytics from '../Analytics';
import UpgradeModal from '../../_components/UpgradeModal';
import EventPageDesigner from '../../_components/EventPageDesigner/EventPageDesigner';

type GuestStats = {
  total: number;
  invited: number;
  attending: number;
  notAttending: number;
  noResponse: number;
};

type State =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'loaded'; event: Event; guestStats?: GuestStats };

interface EventScreenProps {
  onboardingDesign?: InvitationDesign | null;
}

const EventScreen: React.FC<EventScreenProps> = ({ onboardingDesign }) => {
  const { eventCode } = useParams<{ eventCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as 'details' | 'guests' | 'invitations' | 'analytics') || 'details';
  const [state, setState] = useState<State>({ status: 'loading' });
  const [activeView, setActiveView] = useState<'overview' | 'invitation-design' | 'create-invitation-type'>('overview');
  const [activeTab, setActiveTab] = useState<'details' | 'invitations' | 'guests' | 'analytics'>(initialTab);
  const [selectedInvitationType, setSelectedInvitationType] = useState<InvitationType | null>(null);
  const [isEditingNewInvitationType, setIsEditingNewInvitationType] = useState(false);
  const [showCreateInvitationTypeModal, setShowCreateInvitationTypeModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPaymentSuccessBanner, setShowPaymentSuccessBanner] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{invitationType: InvitationType, hasInvitations: boolean} | null>(null);
  
  const handleUpgrade = () => {
    // Close modal and optionally refresh event data
    setShowUpgradeModal(false);
    fetchEvent(); // Refresh to get updated payment status
  };

  const handleDeleteInvitationType = (invitationType: InvitationType) => {
    // Check if this invitation type has any invitations
    const hasInvitations = state.status === 'loaded' && state.event.invitations && 
      state.event.invitations.some(inv => inv.invitationTypeId === invitationType.id);
    
    setDeleteConfirmation({ invitationType, hasInvitations: !!hasInvitations });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;
    
    try {
      await invitationTypesApi.delete(deleteConfirmation.invitationType.id);
      setDeleteConfirmation(null);
      // Refresh event data to show updated invitation types
      fetchEvent();
    } catch (error) {
      console.error('Failed to delete invitation type:', error);
      // You could add error handling/toast here
    }
  };

  const handleUpdateInvitationTypeName = async (id: string, newName: string) => {
    try {
      await invitationTypesApi.update({ id, name: newName });
      // Refresh event data to show updated invitation type
      fetchEvent();
    } catch (error) {
      console.error('Failed to update invitation type name:', error);
      throw error; // Re-throw to let the component handle the error
    }
  };

  // Memoized callback to prevent infinite re-renders
  const handleStatsUpdate = useCallback((stats: GuestStats) => {
    setState(prevState => {
      if (prevState.status === 'loaded') {
        return {
          ...prevState,
          guestStats: stats
        };
      }
      return prevState;
    });
  }, []);

  // Check if we just returned from a successful payment
  useEffect(() => {
    if (location.state?.paymentSuccess) {
      setShowPaymentSuccessBanner(true);
      // Clear the state to prevent showing banner on refresh
      navigate(location.pathname + location.search, { replace: true, state: {} });
      // Auto-hide the banner after 5 seconds
      setTimeout(() => setShowPaymentSuccessBanner(false), 5000);
    }
  }, [location.state, navigate, location.pathname, location.search]);

  const fetchEvent = async () => {
    if (!eventCode) {
      setState({ status: 'error', error: 'No event code provided' });
      return;
    }
    
    setState({ status: 'loading' });
    try {
      // First, fetch all events to find the one with matching code
      const events = await eventsApi.getAll();
      const eventSummary = events.find(e => e.code === eventCode);
      
      if (!eventSummary) {
        throw new Error('Event not found');
      }
      
      // Now fetch the full event data with invitationTypes and invitations
      const event = await eventsApi.getById(eventSummary.id);
      
      if (!event) {
        throw new Error('Event not found');
      }
      
      // Fetch guest stats
      try {
        const guests = await guestsApi.getByEvent(event.id);
        const guestStats: GuestStats = {
          total: guests.length,
          invited: guests.filter(g => g.invitationId).length,
          attending: guests.filter(g => g.invitationId && g.attendance === AttendanceStatus.ATTENDING).length,
          notAttending: guests.filter(g => g.invitationId && g.attendance === AttendanceStatus.NOT_ATTENDING).length,
          noResponse: guests.filter(g => g.invitationId && g.attendance === AttendanceStatus.NO_RESPONSE).length
        };
        console.log('Guest stats fetched:', guestStats);
        setState({ status: 'loaded', event, guestStats });
      } catch (e) {
        // If we can't fetch guests, still show the event
        console.error('Failed to fetch guest stats:', e);
        setState({ status: 'loaded', event });
      }
    } catch (e) {
      setState({ status: 'error', error: e instanceof Error ? e.message : 'Failed to load event' });
    }
  };

  useEffect(() => {
    fetchEvent();
    // eslint-disable-next-line
  }, [eventCode]);
  
  // Update active tab when initialTab changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  return (
    <div className="flex-1 px-4 sm:px-6 lg:px-10 pt-2 pb-4 sm:pb-6 lg:pb-10 font-sans relative bg-gray-50">
      {/* Mixed pink and green gradient background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-bl from-emerald-300/50 via-green-200/40 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 -left-20 w-80 h-80 bg-gradient-to-tr from-rose-300/50 via-pink-200/40 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-gradient-to-tl from-teal-300/40 via-emerald-200/30 to-transparent rounded-full blur-2xl"></div>
      </div>
      
      {/* Content wrapper to ensure it's above the gradient */}
      <div className="relative z-10">
      {state.status === 'loaded' && (
        <>
          {/* Back button */}
          <button
            onClick={() => navigate('/app/events')}
            className="mb-3 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to all events
          </button>
          
          {/* Payment Success Banner */}
          {showPaymentSuccessBanner && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 rounded-full p-2">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-green-900">Payment Successful!</h3>
                  <p className="text-sm text-green-700">Your event has been upgraded and all premium features are now unlocked.</p>
                </div>
                <button
                  onClick={() => setShowPaymentSuccessBanner(false)}
                  className="text-green-600 hover:text-green-800"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          
          <div className="mb-6 flex justify-between items-start">
            <div className="flex items-start gap-6">
              {/* First Invitation Preview */}
              {state.event.invitationTypes && state.event.invitationTypes.length > 0 && state.event.invitationTypes[0].designCustomizations && (
                <div 
                  className="flex-shrink-0 cursor-pointer hover:shadow-lg transition-shadow duration-200 rounded-lg overflow-hidden" 
                  style={{ width: '120px', height: '160px' }}
                  onClick={() => {
                    setSelectedInvitationType(state.event.invitationTypes![0]);
                    setActiveView('invitation-design');
                  }}
                  title="Click to edit this invitation"
                >
                  <div style={{ 
                    width: '120px', 
                    height: '160px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      width: '600px', 
                      height: '800px', 
                      transform: 'scale(0.2)', 
                      transformOrigin: 'top left'
                    }}>
                      <InvitationPreviewV2
                        design={state.event.invitationTypes[0].designCustomizations as InvitationDesign}
                        event={state.event}
                        customDimensions={{ width: 600, height: 800 }}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <div className="flex items-center gap-4 mb-2">
                  <h1 className="text-2xl font-bold text-gray-800">
                    {state.event.eventName}
                  </h1>
                  
                  {/* Paid Status Badge - Only show for paid events */}
                  {state.event.isPaid && (
                    <div className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Paid Event
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Date */}
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-lg">{new Date(state.event.eventDateTime).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
                
                {/* Upgrade CTA for Free Events */}
                {!state.event.isPaid && (
                  <div className="inline-flex items-center gap-3 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-lg px-4 py-2">
                    <div className="bg-rose-100 rounded-full p-1.5">
                      <svg className="w-4 h-4 text-rose-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-sm text-rose-800">
                      Limited to 15 invited guests on the free plan
                    </p>
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="bg-gradient-to-r from-rose-500 to-pink-600 text-white text-xs font-medium px-3 py-1 rounded-md hover:from-rose-600 hover:to-pink-700 transition-all"
                    >
                      Upgrade
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Guest Statistics Card */}
            {state.guestStats && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <table className="text-sm">
                  <tbody>
                    <tr>
                      <td className="text-right pr-3 text-gray-600">Total Guests:</td>
                      <td className="font-bold text-gray-800 text-right w-12">{state.guestStats.total}</td>
                    </tr>
                    <tr>
                      <td className="text-right pr-3 text-gray-600">Invited:</td>
                      <td className="font-bold text-blue-600 text-right w-12">{state.guestStats.invited}</td>
                    </tr>
                    <tr>
                      <td className="text-right pr-3 text-gray-600">Attending:</td>
                      <td className="font-bold text-green-600 text-right w-12">{state.guestStats.attending}</td>
                    </tr>
                    <tr>
                      <td className="text-right pr-3 text-gray-600">Not Attending:</td>
                      <td className="font-bold text-red-600 text-right w-12">{state.guestStats.notAttending}</td>
                    </tr>
                    <tr>
                      <td className="text-right pr-3 text-gray-600">No Response:</td>
                      <td className="font-bold text-gray-400 text-right w-12">{state.guestStats.noResponse}</td>
                    </tr>
                    <tr>
                      <td className="text-right pr-3 text-gray-600">Not Invited:</td>
                      <td className="font-bold text-orange-600 text-right w-12">{state.guestStats.total - state.guestStats.invited}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      
      {state.status === 'loading' && (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center border border-gray-200">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading event details...</div>
        </div>
      )}
      
      {state.status === 'error' && (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center border border-red-100">
          <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-red-600">Failed to load event: {state.error}</div>
        </div>
      )}
      
      {state.status === 'loaded' && activeView === 'overview' && (
        <div data-testid="event-details">
          <span data-testid="event-id" className="hidden">{state.event.id}</span>
          
          {/* Tab Navigation */}
          <div className="bg-white rounded-t-xl shadow-lg border border-gray-200 border-b-0">
            <div className="flex">
              <button
                onClick={() => {
                  setActiveTab('details');
                  setSearchParams({ tab: 'details' });
                }}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'details'
                    ? 'text-gray-900 border-b-2 border-gray-900'
                    : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => {
                  setActiveTab('guests');
                  setSearchParams({ tab: 'guests' });
                }}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'guests'
                    ? 'text-gray-900 border-b-2 border-gray-900'
                    : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                }`}
              >
                Guests
              </button>
              <button
                onClick={() => {
                  setActiveTab('invitations');
                  setSearchParams({ tab: 'invitations' });
                }}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'invitations'
                    ? 'text-gray-900 border-b-2 border-gray-900'
                    : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                }`}
              >
                Invitation Types
              </button>
              <button
                onClick={() => {
                  setActiveTab('analytics');
                  setSearchParams({ tab: 'analytics' });
                }}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'analytics'
                    ? 'text-gray-900 border-b-2 border-gray-900'
                    : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                }`}
              >
                Analytics
              </button>
            </div>
          </div>
          
          {/* Tab Content */}
          <div className="bg-white rounded-b-xl shadow-lg border border-gray-200 border-t-0">
            {activeTab === 'details' && state.status === 'loaded' && (
              <EventPageDesigner eventId={state.event.id} />
            )}
            
            {activeTab === 'guests' && state.status === 'loaded' && (
              <GuestList 
                eventId={state.event.id} 
                eventCode={state.event.code}
                invitationTypes={state.event.invitationTypes || []}
                invitations={state.event.invitations || []}
                onStatsUpdate={handleStatsUpdate}
              />
            )}
            
            {activeTab === 'invitations' && (
              <div className="p-4 sm:p-6 lg:p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Invitation Types</h2>
                  <button
                    onClick={() => setShowCreateInvitationTypeModal(true)}
                    className="px-4 py-2 bg-gray-900 text-white rounded-full hover:bg-gray-800 hover:shadow-md transition-all text-sm sm:text-base font-medium"
                  >
                    Add Invitation Type
                  </button>
                </div>
                
                {/* Using real invitation types from event data */}
                {state.event.invitationTypes && state.event.invitationTypes.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                    {state.event.invitationTypes.map((invType) => {
                      // Check if this invitation type has any invitations
                      const hasInvitations = state.event.invitations && 
                        state.event.invitations.some(inv => inv.invitationTypeId === invType.id);
                      
                      return (
                        <InvitationTypeCard
                          key={invType.id}
                          invitationType={invType}
                          event={state.event}
                          onDesignClick={() => {
                            setSelectedInvitationType(invType);
                            setIsEditingNewInvitationType(false); // Clear flag when editing existing type
                            setActiveView('invitation-design');
                          }}
                          onDelete={() => handleDeleteInvitationType(invType)}
                          canDelete={!hasInvitations}
                          onNameUpdate={handleUpdateInvitationTypeName}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="mb-4">
                      <svg 
                        className="mx-auto h-12 w-12 text-gray-400" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0H4" 
                        />
                      </svg>
                    </div>
                    <p className="text-lg font-medium text-gray-900 mb-2">No invitation types yet</p>
                    <p className="text-sm text-gray-500">Create an invitation type to start designing your invitations.</p>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'analytics' && state.status === 'loaded' && (
              <Analytics eventId={state.event.id} />
            )}
          </div>
        </div>
      )}
      
      {state.status === 'loaded' && activeView === 'invitation-design' && selectedInvitationType && (
        <InvitationDesignPage
          event={state.event}
          invitationType={selectedInvitationType}
          onboardingDesign={onboardingDesign}
          isEditingNewType={isEditingNewInvitationType}
          onBack={() => {
            setActiveView('overview');
            setSelectedInvitationType(null);
            setIsEditingNewInvitationType(false); // Clear flag when going back
          }}
          onDesignSaved={() => {
            // Refresh event data to get updated invitation type with saved design
            fetchEvent();
            setIsEditingNewInvitationType(false); // Clear flag after saving
          }}
        />
      )}


      {/* Create Invitation Type Form */}
      {state.status === 'loaded' && (
        <InvitationTypeForm
          eventId={state.event.id}
          isOpen={showCreateInvitationTypeModal}
          existingTypes={state.event.invitationTypes || []}
          onSuccess={(newInvitationType) => {
            // Refresh the event to get the newly created invitation type
            fetchEvent();
            setShowCreateInvitationTypeModal(false);
            
            // Both scenarios (copied and from scratch) should be treated as "new"
            setSelectedInvitationType(newInvitationType);
            setIsEditingNewInvitationType(true); // Mark as editing a newly created type
            setActiveView('invitation-design');
          }}
          onCancel={() => setShowCreateInvitationTypeModal(false)}
        />
      )}
      
      {/* Upgrade Modal */}
      {state.status === 'loaded' && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={handleUpgrade}
          currentEvent={state.event}
          eventId={state.event.id}
          currentGuestCount={state.guestStats?.total || 0}
          maxFreeGuests={15}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.15)', backdropFilter: 'blur(2px)' }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {deleteConfirmation.hasInvitations ? 'Cannot Delete Invitation Type' : 'Delete Invitation Type'}
              </h3>
              
              {deleteConfirmation.hasInvitations ? (
                <p className="text-gray-600">
                  Cannot delete "{deleteConfirmation.invitationType.name}" because it has existing invitations. 
                  Please delete all invitations using this type first.
                </p>
              ) : (
                <p className="text-gray-600">
                  Are you sure you want to delete "{deleteConfirmation.invitationType.name}"? 
                  This action cannot be undone.
                </p>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {deleteConfirmation.hasInvitations ? 'OK' : 'Cancel'}
              </button>
              
              {!deleteConfirmation.hasInvitations && (
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};


export default EventScreen; 